import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useMeetingStore } from '../../../store/meetingStore';
import { useTaskStore } from '../../../store/taskStore';

type CalEvent = { label: string; kind: 'meeting' | 'task'; tone: string; onClick?: () => void };

const toKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function WorkCalendar() {
    const navigate = useNavigate();
    const { meetings, fetchMeetings } = useMeetingStore();
    const { tasks, fetchTasks } = useTaskStore();
    const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

    useEffect(() => {
        fetchMeetings();
        fetchTasks(); // all tasks
    }, []);

    // Map events by day key for the visible period.
    const eventsByDay = useMemo(() => {
        const map: Record<string, CalEvent[]> = {};
        const push = (key: string, ev: CalEvent) => {
            map[key] ||= [];
            map[key].push(ev);
        };

        for (const m of meetings) {
            if (!m.startTime) continue;
            const key = toKey(new Date(m.startTime));
            push(key, { label: m.title, kind: 'meeting', tone: 'bg-info/20 text-info', onClick: () => navigate('/work/meetings') });
        }
        for (const t of tasks) {
            if (!t.dueDate) continue;
            const key = toKey(new Date(t.dueDate));
            const tone = t.priority === 'urgent' || t.priority === 'high' ? 'bg-destructive/20 text-destructive' : 'bg-warning/20 text-warning';
            push(key, { label: t.title, kind: 'task', tone, onClick: () => navigate(`/work/tasks/${t.id}`) });
        }
        return map;
    }, [meetings, tasks, navigate]);

    // Build the month grid (leading blanks + days).
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayKey = toKey(new Date());

    const cells: (Date | null)[] = [
        ...Array.from({ length: firstWeekday }, () => null),
        ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
    ];
    while (cells.length % 7 !== 0) cells.push(null);

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Work Calendar</h1>
                    <p className="text-muted-foreground">Meetings and task deadlines across your workspace.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="primary" onClick={() => navigate('/work/meetings')}>
                        <Plus className="w-4 h-4 mr-2" />
                        New Meeting
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap gap-4 shrink-0 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-info/40" /> Meeting</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-warning/40" /> Task due</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-destructive/40" /> High-priority task due</span>
            </div>

            <Card className="flex-1 flex flex-col min-h-0">
                <CardHeader className="flex flex-row justify-between items-center py-3 shrink-0 border-b border-border">
                    <h3 className="text-foreground font-display text-section-title">{MONTHS[month]} {year}</h3>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Today</Button>
                        <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></Button>
                        <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} aria-label="Next month"><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                </CardHeader>
                <CardContent className="flex-1 p-0 overflow-auto bg-card flex flex-col">
                    <div className="grid grid-cols-7 border-b border-border bg-secondary/50 shrink-0">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <div key={day} className="py-2 text-center text-sm font-medium text-muted-foreground border-r border-border last:border-r-0">
                                {day}
                            </div>
                        ))}
                    </div>
                    <div className="flex-1 grid grid-cols-7 bg-border gap-px" style={{ gridAutoRows: 'minmax(110px, 1fr)' }}>
                        {cells.map((cell, i) => {
                            if (!cell) return <div key={`empty-${i}`} className="bg-secondary/30" />;
                            const key = toKey(cell);
                            const isToday = key === todayKey;
                            const dayEvents = eventsByDay[key] || [];
                            return (
                                <div key={key} className={`bg-card p-2 flex flex-col gap-1 overflow-hidden ${isToday ? 'ring-1 ring-primary ring-inset bg-primary/5' : ''}`}>
                                    <span className={`text-sm ${isToday ? 'font-semibold text-primary' : 'text-foreground font-medium'}`}>{cell.getDate()}</span>
                                    {dayEvents.slice(0, 3).map((ev, idx) => (
                                        <button
                                            key={idx}
                                            onClick={ev.onClick}
                                            className={`text-xs p-1 rounded font-medium truncate text-left ${ev.tone} hover:opacity-80`}
                                            title={ev.label}
                                        >
                                            {ev.label}
                                        </button>
                                    ))}
                                    {dayEvents.length > 3 && (
                                        <span className="text-xs text-muted-foreground">+{dayEvents.length - 3} more</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
